import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { Dropdown, Empty, Modal, Space, Table, Typography } from 'antd'
import Button from '../../common/Button'
import DtcMappings from './DtcMappings'
import { useNavigate } from 'react-router-dom'
import {
  EllipsisOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { TatContext } from './context/context'
import { useIntl, FormattedMessage } from 'react-intl'
import RecordsCount from '../../common/RecordsCount'

const DtcTable = ({
  dtcs,
  pagination,
  getPaginatedData,
  vehicleSid,
  hasNoConnectorMapping,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [state, dispatch] = useContext(TatContext)
  const [hasNoConnectorMappings, setHasNoConnectorMappings] = useState(null)
  const { currentPage, totalCount, totalPages, nextPageLink, prevPageLink } =
    pagination
  const [modal, contextHolder] = Modal.useModal()

  useEffect(() => {
    setHasNoConnectorMappings(hasNoConnectorMapping === 'yes')
  }, [dtcs])

  const dataSource = dtcs.map((dtc, index) => {
    return {
      id: dtc.id,
      dtcName: dtc.name,
      shortDescription: dtc.short_description,
      // description: dtc.description,
      ecu: dtc.ecu,
      active: dtc.active,
      destinationOnly: dtc.destination_only,

      mappings: dtc.mappings,
      startConnectorMappings: dtc.mappings?.start || [],
      endConnectorMappings: dtc.mappings?.end || [],
      devices: dtc.mappings?.devices || [],
    }
  })

  const columns = [
    {
      title: intl.formatMessage({ id: 'dtcs.listing.dtcName' }),
      dataIndex: 'dtcName',
      sorter: (a, b) => a.dtcName.localeCompare(b.dtcName),
    },
    {
      title: intl.formatMessage({ id: 'generic.description' }),
      dataIndex: 'shortDescription',
    },
    /* {
      title: 'Description',
      dataIndex: 'description',
    }, */
    {
      title: intl.formatMessage({ id: 'generic.ECU' }),
      dataIndex: 'ecu',
    },
    {
      title: intl.formatMessage({ id: 'generic.active' }),
      dataIndex: 'active',
      render: (value) =>
        value
          ? intl.formatMessage({ id: 'generic.yes' })
          : intl.formatMessage({ id: 'generic.no' }),
    },
    {
      title: intl.formatMessage({ id: 'dtcs.listing.destinationOnly' }),
      dataIndex: 'destinationOnly',
      render: (value) =>
        value
          ? intl.formatMessage({ id: 'generic.yes' })
          : intl.formatMessage({ id: 'generic.no' }),
    },
    {
      title: '',
      width: 'auto',
      align: 'right',
      render: (_, record) => {
        const mappingsCount = Object.keys(record.mappings ?? {}).length === 0
          ? 0
          : record.startConnectorMappings.length +
            record.endConnectorMappings.length +
            record.devices.length
        const isExpanded = expandedRowKeys.includes(record.id)
        const editUrl = `/vehicles/${vehicleSid}/dtcs/listing/${record.id}/edit`
        const cloneUrl = `/vehicles/${vehicleSid}/dtcs/listing/${record.id}/clone`
        const addDependencyUrl = `/vehicles/${vehicleSid}/dtcs/listing/${record.id}/add_dependency`
        /* Clone & Add New Dependency button */
        const moreMenuItems = [
          {
            key: 'edit',
            label: (
              <span data-testid="dtc-more-option" data-action="edit">
                {intl.formatMessage({ id: 'dtcs.listing.edit' })}
              </span>
            ),
            className: 'dd-menu-item edit-dtc-btn',
          },
          {
            key: 'clone',
            label: (
              <span data-testid="dtc-more-option" data-action="clone">
                {intl.formatMessage({ id: 'dtcs.listing.clone' })}
              </span>
            ),
            className: 'dd-menu-item clone-dtc-btn',
          },
          {
            key: 'add_new_dependency',
            label: (
              <span data-testid="dtc-more-option" data-action="add_new_dependency">
                {intl.formatMessage({ id: 'dtcs.listing.addNewDependency' })}
              </span>
            ),
            className: 'dd-menu-item add-new-dependency-btn',
          },
        ]

        return (
          <Space size="middle">
            {!hasNoConnectorMappings && mappingsCount !== 0 && (
              <Button
                type="link"
                className={`${isExpanded ? 'link-button neutral' : 'link-button'}`}
                onClick={() => toggleExpand(record.id)}
                testId="dtc-toggle-mappings"
              >
                {`${isExpanded ? intl.formatMessage({ id: 'dtcs.listing.hideMappings' }) : intl.formatMessage({ id: 'dtcs.listing.showMappings' })}, ${mappingsCount}`}
              </Button>
            )}

            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlayStyle={{
                marginTop: 12,
              }}
              menu={{
                items: moreMenuItems,
                onClick: ({ key }) => {
                  if (key === 'edit') navigate(editUrl)
                  if (key === 'clone') navigate(cloneUrl)
                  if (key === 'add_new_dependency') navigate(addDependencyUrl)
                },
              }}
            >
              <Button icon={<MoreOutlined />} testId="dtc-more" />
            </Dropdown>
          </Space>
        )
      },
    },
  ]

  /* Handle Show / Hide DTC Connector Mappings */
  const expandedRowRender = (record) => {
    return (
      <div className="dtc-expanded" data-testid="dtc-expanded-mappings">
        <DtcMappings
          title={intl.formatMessage({ id: 'generic.start' })}
          mappings={record.startConnectorMappings}
        />
        <DtcMappings
          title={intl.formatMessage({ id: 'generic.end' })}
          mappings={record.endConnectorMappings}
        />
        <DtcMappings
          title={intl.formatMessage({ id: 'generic.devices' })}
          mappings={record.devices}
        />
      </div>
    )
  }

  const toggleExpand = (id) => {
    setExpandedRowKeys((prev) => {
      const keys = prev.includes(id)
        ? prev.filter((key) => key !== id)
        : [...prev, id]
      return keys
    })
  }
  /* Handle Table Row selection */
  const selectedIds = useMemo(() => {
    return state.selected_dtc.map((item) => item.id)
  }, [state.selected_dtc])

  const rowSelection = {
    selectedRowKeys: [...selectedIds],
    hideSelectAll: true,
    onSelect: (record, selected) => {
      if (selected) {
        const hasDifferentType = state.selected_dtc.find(
          (item) => item.destination_only !== record.destinationOnly
        )

        if (hasDifferentType) {
          showDestinationOnlyError()
          return
        }
      }

      dispatch({
        type: 'select_dtc',
        data: {
          id: record.id,
          destination_only: record.destinationOnly,
          name: record.dtcName,
          ecu: record.ecu,
        },
      })
    },
  }

  const showDestinationOnlyError = () => {
    modal.info({
      icon: <ExclamationCircleOutlined />,
      title: intl.formatMessage({ id: 'forms.info' }),
      content: <>{intl.formatMessage({ id: 'dtcs.listing.cantSelectBoth' })}</>,
      centered: true,
      maskClosable: true,
      onOk: () => {},
      width: '33%',
    })
  }

  return (
    <>
      {contextHolder}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        rowSelection={rowSelection}
        expandable={{
          expandIcon: () => null,
          showExpandColumn: false,
          expandedRowKeys,
          expandedRowRender,
          rowExpandable: () => true,
        }}
        pagination={{
          current: currentPage,
          total: totalCount,
          pageSize: 10,
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total, range) => <RecordsCount count={total} />,
          onChange: (page) => {
            // 10 is page size
            const offset = (page - 1) * 10
            const url = nextPageLink
              ? new URL(nextPageLink)
              : new URL(prevPageLink)
            url.searchParams.set('offset', offset)
            getPaginatedData(url.toString())
          },
        }}
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={ <RecordsCount count={0} /> }  />
            </div>
          ),
        }}
      />
      {totalPages <= 1 && totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={totalCount} />
        </Typography.Text>
      )}
    </>
  )
}

DtcTable.propTypes = {}

export default DtcTable
